import React from "react";
import { GET_CLIENT_TRANSACTIONS_QUERY } from "../../graphql/queries/transactions";
import { useQuery, useLazyQuery } from "@apollo/client";
import Loader from "../../components/loader/loader";
import Table from "../../components/table/table";
import TableCellRenderer from "../../components/table/table-cell-renderer";
import { GET_CLIENT_QUERY } from "../../graphql/queries/clients";
import { dateToString, formatTimestamp } from "../../utils/date-converter";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import { useState, useEffect } from "react";
import Modal from "../../components/modal/Modal";
import EditClient from "../../components/edit-client/edit-client";
import { FIRST, SKIP } from "../../utils/pagination";
import { GET_CLIENT_METER_SETTING_EVENTS_QUERY } from "../../graphql/queries/meterSettingEvents";
import {
  METER_SETTING_EVENTS,
  UPDATES_PAGE_SIZE,
} from "../../utils/meter-setting-events";
import useUserRoleStore from "../../store/user-role";
import { isAdmin } from "../../utils/roles";
import Tabs from "../../components/tabs/tabs";

const TRANSACTIONS_TAB = "transactions";
const UPDATES_TAB = "updates";

const ClientTransactions = () => {
  const { role } = useUserRoleStore();
  const canEdit = isAdmin(role);
  const [activeTab, setActiveTab] = useState(TRANSACTIONS_TAB);
  const [editClientModal, setEditClientModal] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    hasNextPage: undefined,
    hasPreviousPage: undefined,
    totalPages: undefined,
    totalRecords: undefined,
    currentPage: 1,
  });
  const [sortField, setSortField] = useState("timestamp"); // default sort field
  const [sortOrder, setSortOrder] = useState("desc"); // default sort order
  const params = useParams();
  const navigate = useNavigate();
  const [fetchClientTransactions, { data, loading, error }] = useLazyQuery(
    GET_CLIENT_TRANSACTIONS_QUERY,
    {
      variables: {
        clientId: parseInt(params.id, 10),
        first: FIRST,
        skip: (paginationInfo.currentPage - 1) * SKIP,
        sortField: sortField,
        sortOrder: sortOrder,
      },
    }
  );

  const [updatesPage, setUpdatesPage] = useState(1);

  const {
    data: updatesData,
    loading: updatesLoading,
    error: updatesError,
  } = useQuery(GET_CLIENT_METER_SETTING_EVENTS_QUERY, {
    variables: {
      clientId: parseInt(params.id, 10),
      first: UPDATES_PAGE_SIZE,
      skip: (updatesPage - 1) * UPDATES_PAGE_SIZE,
    },
    // Only fetched once the tab is actually opened
    skip: !canEdit || activeTab !== UPDATES_TAB,
  });

  const updates = updatesData?.clientMeterSettingEvents;

  const tabs = [
    { key: TRANSACTIONS_TAB, label: "Recent Transactions" },
    ...(canEdit ? [{ key: UPDATES_TAB, label: "Recent Updates" }] : []),
  ];

  const clientResponse = useQuery(GET_CLIENT_QUERY, {
    variables: {
      clientId: parseInt(params.id, 10),
    },
  });

  useEffect(() => {
    fetchClientTransactions();
  }, [sortField, sortOrder]);

  const onEditClient = () => {
    setEditClientModal((prev) => !prev);
  };

  const handleBackToDashboard = () => {
    navigate("/clients-dashboard");
  };

  useEffect(() => {
    if (data) {
      setPaginationInfo((prev) => ({
        ...prev,
        currentPage: data.clientTransactions.currentPage,
        hasNextPage: data.clientTransactions.hasNextPage,
        hasPreviousPage: data.clientTransactions.hasPreviousPage,
        totalPages: data.clientTransactions.totalPages,
        totalRecords: data.clientTransactions.totalRecords,
      }));
    }
  }, [data]);

  const onNextPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage + 1,
    }));
  };

  const onUpdatesNextPageClick = () => {
    setUpdatesPage((prev) => prev + 1);
  };

  const onUpdatesPrevPageClick = () => {
    setUpdatesPage((prev) => prev - 1);
  };

  const onPrevPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage - 1,
    }));
  };

  const handleSortChange = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1, // Reset pagination when sort changes
    }));
  };

  if (loading || clientResponse.loading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (error || clientResponse.error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  return (
    <div className="display-page">
      <Modal onClose={onEditClient} modal={editClientModal} modalSize="large">
        <>
          <EditClient
            onCancel={onEditClient}
            clientData={clientResponse.data?.client}
            clientId={parseInt(params.id, 10)}
            clientResponse={clientResponse}
          />
        </>
      </Modal>
      <div className="display-page__details-wrapper">
        <div className="display-page__column-left">
          <div className="display-page__details-action">
            <Button
              onClick={handleBackToDashboard}
              name={"Back to Dashboard"}
              className={"button--secondary"}
            />
            {canEdit && (
              <Button
                onClick={onEditClient}
                name={"Edit"}
                className={"button--action"}
              />
            )}
          </div>
          <div className="display-page__details">
            <div className="display-page__details-row">
              <div className="display-page__details-key">Name</div>
              <div className="display-page__details-value">
                {`${clientResponse.data.client.name} ${clientResponse.data.client.surname}`}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Client ID</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.clientId}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Channel</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.tower.zonkeId} -{" "}
                {clientResponse.data.client.channel}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Package</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.package.name}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Mobile</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.phoneNumber}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Contact Method</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.contactMethod}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">W3W Address</div>
              <div className="display-page__details-value">
                {clientResponse.data.client.w3w}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Date Added</div>
              <div className="display-page__details-value">
                {dateToString(clientResponse.data.client.createdDate)}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">Balance</div>
              <div className="display-page__details-value">
                R{clientResponse.data.client.balance.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div className="display-page__graph"></div>
      </div>
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      {activeTab === TRANSACTIONS_TAB && (
        <>
          <div className="display-page__info-wrapper">
            Displaying{" "}
            <span className="display-page__info--bold">
              {paginationInfo.currentPage === 1
                ? 1
                : (paginationInfo.currentPage - 1) * SKIP + 1}{" "}
            </span>
            to{" "}
            <span className="display-page__info--bold">
              {Math.min(
                paginationInfo.currentPage * SKIP,
                paginationInfo.totalRecords
              )}
            </span>{" "}
            of{" "}
            <span className="display-page__info--bold">
              {paginationInfo.totalRecords}
            </span>{" "}
            total.
          </div>
          <div className="display-page__table">
            <Table
              data={data?.clientTransactions?.items}
              columns={[
                {
                  field: "timestamp",
                  title: "Date",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ timestamp }) => {
                    return (
                      <TableCellRenderer
                        firstItem={`${formatTimestamp(timestamp).date} ${
                          formatTimestamp(timestamp).time
                        }`}
                      />
                    );
                  },
                  sortable: true,
                },
                {
                  field: "type",
                  title: "Type",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ purchaseType }) => {
                    return <TableCellRenderer firstItem={purchaseType} />;
                  },
                },
                {
                  field: "amount",
                  title: "Amount",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ amount }) => {
                    return <TableCellRenderer firstItem={amount.toFixed(2)} />;
                  },
                  sortable: true,
                },
                {
                  field: "vendor",
                  title: "Vendor",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ vendor }) => {
                    return <TableCellRenderer firstItem={vendor} />;
                  },
                },
                {
                  field: "platform",
                  title: "Platform",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ platform }) => {
                    return <TableCellRenderer firstItem={platform} />;
                  },
                },
                {
                  field: "success",
                  title: "Success",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ success }) => {
                    return (
                      <TableCellRenderer
                        firstItem={success ? "YES" : "NO"}
                        firstItemClass={success === true ? "success" : "danger"}
                      />
                    );
                  },
                },
                {
                  field: "transactionId",
                  title: "Transaction ID",
                  showOnMobile: true,
                  hideMobileUp: false,
                  render: ({ id }) => {
                    return <TableCellRenderer firstItem={id} />;
                  },
                },
              ]}
              paged
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPreviousPage}
              onNextPage={onNextPageClick}
              onPrevPage={onPrevPageClick}
              sortConfig={{ key: sortField, direction: sortOrder }}
              onSortChange={handleSortChange}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "15px 20px",
                fontWeight: "bold",
                fontSize: "16px",
                backgroundColor: "#f7f7f7",
                borderTop: "2px solid #d4d4ea",
              }}
            >
              <span style={{ marginRight: "10px" }}>Client Balance:</span>
              <span style={{ color: "#f6d13d" }}>
                R{clientResponse.data.client.balance.toLocaleString()}
              </span>
            </div>
          </div>
        </>
      )}
      {canEdit && activeTab === UPDATES_TAB && (
        <>
          {updatesLoading ? (
            <Loader sizeClass={"size--medium"} />
          ) : updatesError ? (
            <div className="display-page__error">
              Could not load recent updates
            </div>
          ) : (
            <>
              <div className="display-page__info-wrapper">
                Displaying{" "}
                <span className="display-page__info--bold">
                  {updates?.totalRecords
                    ? (updates.currentPage - 1) * UPDATES_PAGE_SIZE + 1
                    : 0}{" "}
                </span>
                to{" "}
                <span className="display-page__info--bold">
                  {Math.min(
                    (updates?.currentPage ?? 1) * UPDATES_PAGE_SIZE,
                    updates?.totalRecords ?? 0
                  )}
                </span>{" "}
                of{" "}
                <span className="display-page__info--bold">
                  {updates?.totalRecords ?? 0}
                </span>{" "}
                total.
              </div>
              <div className="display-page__table">
                <Table
                  data={updates?.items ?? []}
                  columns={[
                    {
                      field: "timestamp",
                      title: "Date",
                      showOnMobile: true,
                      hideMobileUp: false,
                      render: ({ timestamp }) => {
                        return (
                          <TableCellRenderer
                            firstItem={`${formatTimestamp(timestamp).date} ${
                              formatTimestamp(timestamp).time
                            }`}
                          />
                        );
                      },
                    },
                    {
                      field: "event",
                      title: "Event",
                      showOnMobile: true,
                      hideMobileUp: false,
                      render: ({ event }) => {
                        return (
                          <TableCellRenderer
                            firstItem={METER_SETTING_EVENTS[event] ?? event}
                          />
                        );
                      },
                    },
                    {
                      field: "previousValue",
                      title: "Previous Value",
                      showOnMobile: true,
                      hideMobileUp: false,
                      render: ({ previousValue }) => {
                        return (
                          <TableCellRenderer firstItem={previousValue || "-"} />
                        );
                      },
                    },
                    {
                      field: "currentValue",
                      title: "Current Value",
                      showOnMobile: true,
                      hideMobileUp: false,
                      render: ({ currentValue }) => {
                        return (
                          <TableCellRenderer firstItem={currentValue || "-"} />
                        );
                      },
                    },
                    {
                      field: "appUser",
                      title: "User",
                      showOnMobile: true,
                      hideMobileUp: false,
                      render: ({ appUser }) => {
                        return <TableCellRenderer firstItem={appUser} />;
                      },
                    },
                  ]}
                  paged
                  hasNextPage={updates?.hasNextPage}
                  hasPrevPage={updates?.hasPreviousPage}
                  onNextPage={onUpdatesNextPageClick}
                  onPrevPage={onUpdatesPrevPageClick}
                  onSortChange={() => {}}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ClientTransactions;
